/**
 * The host side of Unity embedded in the editor (ADR-058 D3, D4, D8): Unity runs in a frame behind the steps and signs in with
 * OUR access token, because a framed Unity cannot reach Keycloak itself.
 *
 * Red zone (a token crosses a window boundary). The rules, each enforced here and tested:
 *  - we post only to the exact Unity origin we loaded, never `"*"`;
 *  - we accept a message only from that frame's window AND that origin;
 *  - we speak first (`hello`), since Unity cannot read our origin; it answers `ready`;
 *  - the token goes nowhere else: not into a URL, a log or an error.
 */

export const EMBED_PROTOCOL = 1;

export type EmbedErrorCode = "issuer" | "timeout" | "realm" | "sessionEnded";

export interface EmbedToken {
  accessToken: string;
  /** Seconds the token has LEFT now, not its full lifetime (ADR-058 D3): Unity counts from receipt. */
  expiresIn: number;
  issuer: string;
}

export interface EmbedHostOptions {
  /** The frame Unity runs in. Read on every message, as the element may be replaced. */
  frame: () => HTMLIFrameElement | null;
  /** The exact origin of the Unity build we loaded. */
  unityOrigin: string;
  /** The current token, refreshed if needed; `null` when the session is gone. */
  getToken: () => Promise<EmbedToken | null>;
  onError: (code: EmbedErrorCode) => void;
  /** Unity answered: a fresh handshake, so an earlier error no longer applies. */
  onReady?: () => void;
  win?: Window;
  /** How often `hello` repeats until Unity answers; its listener starts late. */
  helloEveryMs?: number;
}

const ERROR_CODES = new Set<EmbedErrorCode>(["issuer", "timeout", "realm", "sessionEnded"]);

/** The free part of the frame next to the steps, in CSS pixels from the frame's top left. */
export interface EmbedRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface EmbedHost {
  dispose: () => void;
  /**
   * 3D TEST prototype (owner 2026-11-03, design-notes.md): select the XR element in Unity and frame it in `rect`, where the
   * author can orbit around it. Until a token has gone out after Unity's `ready`, only the latest pick is kept, and sent right after the token.
   */
  focusElement: (elementId: number, rect: EmbedRect) => void;
}

export function startEmbedHost(opts: EmbedHostOptions): EmbedHost {
  const win = opts.win ?? window;
  const every = opts.helloEveryMs ?? 1000;
  let ready = false;
  let disposed = false;
  let hello: number | undefined;
  // A focus asked for before Unity was ready (it loads for a while): sent once it is, so the author's pick is not lost.
  let pendingFocus: { elementId: number; rect: EmbedRect } | null = null;
  // Unity takes a focus only once signed in, so it is held until a token has gone out after the latest `ready`.
  let signedIn = false;
  // The last focus sent: a reloaded Unity has forgotten it and gets it again after its new token.
  let lastFocus: { elementId: number; rect: EmbedRect } | null = null;

  const post = (message: Record<string, unknown>) => {
    const target = opts.frame()?.contentWindow;
    if (!target || disposed) return;
    target.postMessage(JSON.stringify({ ...message, protocol: EMBED_PROTOCOL }), opts.unityOrigin);
  };

  const sendToken = async () => {
    const token = await opts.getToken().catch(() => null);
    if (disposed) return;
    if (token === null) {
      post({ type: "xrc.embed.signedOut" });
      signedIn = false;
      return;
    }
    post({ type: "xrc.embed.token", accessToken: token.accessToken, expiresIn: token.expiresIn, issuer: token.issuer });
    signedIn = true;
    if (pendingFocus !== null) {
      postFocus(pendingFocus);
      pendingFocus = null;
    }
  };
  function postFocus(focus: { elementId: number; rect: EmbedRect }) {
    lastFocus = focus;
    post({ type: "xrc.embed.focusElement", elementId: focus.elementId, rect: focus.rect, pixelRatio: win.devicePixelRatio || 1 });
  }

  // From the start, and again whenever the frame (re)loads: a reloaded Unity has forgotten us and waits for a new hello.
  const startHello = () => {
    win.clearInterval(hello);
    ready = false;
    signedIn = false;
    pendingFocus ??= lastFocus;
    // No cut-off: the build is large and may take minutes over a slow line; a hello a second until it answers costs nothing.
    hello = win.setInterval(() => {
      if (ready) {
        win.clearInterval(hello);
        return;
      }
      post({ type: "xrc.embed.hello" });
    }, every);
  };
  startHello();
  const frameElement = opts.frame();
  frameElement?.addEventListener("load", startHello);

  const onMessage = (e: MessageEvent) => {
    const frameWindow = opts.frame()?.contentWindow;
    if (!frameWindow || e.source !== frameWindow || e.origin !== opts.unityOrigin) return;
    let data: { type?: unknown; protocol?: unknown; code?: unknown };
    try {
      data = typeof e.data === "string" ? (JSON.parse(e.data) as typeof data) : (e.data as typeof data);
    } catch {
      return;
    }
    if (data == null || data.protocol !== EMBED_PROTOCOL) return;
    switch (data.type) {
      case "xrc.embed.ready":
        ready = true; // a reload of the frame says ready again and gets a token again
        signedIn = false;
        opts.onReady?.();
        void sendToken();
        break;
      case "xrc.embed.tokenRequest":
        void sendToken();
        break;
      case "xrc.embed.error":
        if (ERROR_CODES.has(data.code as EmbedErrorCode)) opts.onError(data.code as EmbedErrorCode);
        break;
      default:
        break; // not ours (the panel bridge uses other names)
    }
  };
  win.addEventListener("message", onMessage);

  return {
    dispose: () => {
      disposed = true;
      win.clearInterval(hello);
      win.removeEventListener("message", onMessage);
      frameElement?.removeEventListener("load", startHello);
    },
    focusElement: (elementId, rect) => {
      if (signedIn) postFocus({ elementId, rect });
      else pendingFocus = { elementId, rect }; // only the latest pick counts
    },
  };
}
